/**
 * normalizeFinancialExtraction
 * 
 * Normalizes financial extraction data from multiple schema formats into a consistent structure.
 * Supports both nested schema and flat metrics array schema.
 * 
 * EBITDA DEFINITION UNIFIER:
 * All modules use the same Base EBITDA selected deterministically:
 * 1) QoE Adjusted (if sourced with evidence)
 * 2) Seller Adjusted (if sourced)
 * 3) Reported (if sourced)
 * 4) Missing (with reason)
 */

import { selectBaseEbitda, isSourcedEbitda } from './models/ebitda';

/**
 * Helper to get latest period value from array of period values
 * @param {Array} arr - Array of {period, value} objects
 * @returns {Object|null} - The latest period object with value and period label
 */
export function getLatestPeriod(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  
  const sorted = [...arr].sort((a, b) => {
    const aStr = (a.period || '').toString().toUpperCase();
    const bStr = (b.period || '').toString().toUpperCase();
    
    // Prefer LTM first
    if (aStr.includes('LTM') && !bStr.includes('LTM')) return -1;
    if (!aStr.includes('LTM') && bStr.includes('LTM')) return 1;
    
    // Then sort by period string (FY2024 > FY2023)
    return bStr.localeCompare(aStr);
  });
  
  return sorted[0];
}

/**
 * Get just the value from the latest period
 * @param {Array} arr - Array of {period, value} objects
 * @returns {number|null}
 */
export function getLatestValue(arr) {
  const period = getLatestPeriod(arr);
  return period?.value ?? null;
}

/**
 * Get period label for display
 * @param {Array} arr - Array of {period, value} objects
 * @returns {string|null}
 */
export function getPeriodLabel(arr) {
  const period = getLatestPeriod(arr);
  return period?.period ?? null;
}

/**
 * Calculate gross margin if missing
 * @param {number} grossProfit 
 * @param {number} revenue 
 * @returns {Object|null} - { value, calculated: boolean }
 */
export function calculateGrossMargin(grossProfit, revenue) {
  if (grossProfit === null || grossProfit === undefined || !revenue) {
    return null;
  }
  return {
    value: grossProfit / revenue,
    calculated: true
  };
}

/**
 * Main normalization function
 * Handles both nested schema and flat metrics array schema
 * 
 * @param {Object} raw - Raw financial extraction data
 * @returns {Object} Normalized financial data
 */
export function normalizeFinancialExtraction(raw) {
  if (!raw) {
    return createEmptyFinancialData();
  }

  // Check if this is flat metrics array schema
  if (raw.metrics && Array.isArray(raw.metrics)) {
    return normalizeFlatMetrics(raw.metrics);
  }

  // Nested schema normalization
  return normalizeNestedSchema(raw);
}

function normalizeNestedSchema(raw) {
  // Revenue
  const revenueArr = raw.revenue || [];
  const revenuePeriod = getLatestPeriod(revenueArr);
  
  // Gross Profit
  const grossProfitArr = raw.gross_profit || [];
  const grossProfitPeriod = getLatestPeriod(grossProfitArr);
  
  // Calculate gross margin if missing
  let grossMarginValue = getLatestValue(raw.gross_margin);
  let grossMarginCalculated = false;
  
  if ((grossMarginValue === null || grossMarginValue === undefined) && 
      grossProfitPeriod?.value && revenuePeriod?.value) {
    const calculated = calculateGrossMargin(grossProfitPeriod.value, revenuePeriod.value);
    if (calculated) {
      grossMarginValue = calculated.value;
      grossMarginCalculated = true;
    }
  }

  // EBITDA - prefer reported, then any variant
  const ebitdaObj = raw.ebitda || {};
  let ebitdaValue = null;
  let ebitdaVariant = null;
  let ebitdaPeriod = null;
  
  const ebitdaPriority = ['reported', 'management_adjusted', 'seller_adjusted', 'qoe_adjusted', 'pro_forma_synergized'];
  
  for (const variant of ebitdaPriority) {
    if (ebitdaObj[variant] && Array.isArray(ebitdaObj[variant]) && ebitdaObj[variant].length > 0) {
      const period = getLatestPeriod(ebitdaObj[variant]);
      if (period?.value !== null && period?.value !== undefined) {
        ebitdaValue = period.value;
        ebitdaVariant = variant;
        ebitdaPeriod = period.period;
        break;
      }
    }
  }

  // Get all EBITDA variants for bridge display
  const ebitdaVariants = {};
  Object.keys(ebitdaObj).forEach(variant => {
    const val = getLatestValue(ebitdaObj[variant]);
    if (val !== null && val !== undefined) {
      ebitdaVariants[variant] = {
        value: val,
        period: getPeriodLabel(ebitdaObj[variant]),
        source: 'Document',
        evidence: [] // Evidence would be populated from AI extraction
      };
    }
  });

  // Build canonical EBITDA structure for Base EBITDA selection
  const ebitdaFinancials = {
    reported: ebitdaVariants.reported || null,
    sellerAdjusted: ebitdaVariants.seller_adjusted || null,
    qoeAdjusted: ebitdaVariants.qoe_adjusted || null
  };
  const baseEbitda = selectBaseEbitda(ebitdaFinancials);

  // Customer concentration
  const customerConcObj = raw.customer_concentration || {};
  let customerConcValue = null;
  let customerConcLabel = null;
  let customerConcPeriod = null;
  
  if (customerConcObj.top_1?.value !== undefined) {
    customerConcValue = customerConcObj.top_1.value;
    customerConcLabel = 'Top 1';
    customerConcPeriod = customerConcObj.top_1.period;
  } else if (customerConcObj.top_3?.value !== undefined) {
    customerConcValue = customerConcObj.top_3.value;
    customerConcLabel = 'Top 3';
    customerConcPeriod = customerConcObj.top_3.period;
  } else if (customerConcObj.top_5?.value !== undefined) {
    customerConcValue = customerConcObj.top_5.value;
    customerConcLabel = 'Top 5';
    customerConcPeriod = customerConcObj.top_5.period;
  }

  return {
    revenue: {
      value: revenuePeriod?.value ?? null,
      period: revenuePeriod?.period ?? null
    },
    grossProfit: {
      value: grossProfitPeriod?.value ?? null,
      period: grossProfitPeriod?.period ?? null
    },
    grossMargin: {
      value: grossMarginValue,
      period: revenuePeriod?.period ?? null,
      calculated: grossMarginCalculated
    },
    ebitda: {
      value: ebitdaValue,
      period: ebitdaPeriod,
      variant: ebitdaVariant,
      variants: ebitdaVariants,
      base: baseEbitda  // Unified Base EBITDA for all calculations
    },
    netDebt: {
      value: raw.net_debt?.value ?? null,
      period: raw.net_debt?.period ?? null
    },
    revolverAvailability: {
      value: raw.revolver_availability?.value ?? null,
      period: raw.revolver_availability?.period ?? null
    },
    dso: {
      value: getLatestValue(raw.dso_days),
      period: getPeriodLabel(raw.dso_days)
    },
    customerConcentration: {
      value: customerConcValue,
      label: customerConcLabel,
      period: customerConcPeriod
    },
    customerCount: {
      value: getLatestValue(raw.customer_count),
      period: getPeriodLabel(raw.customer_count)
    },
    overtimePctPayroll: {
      value: getLatestValue(raw.overtime_pct_payroll),
      period: getPeriodLabel(raw.overtime_pct_payroll)
    },
    leaseExpense: {
      value: getLatestValue(raw.lease_expense),
      period: getPeriodLabel(raw.lease_expense)
    },
    insuranceExpense: {
      value: getLatestValue(raw.insurance_expense),
      period: getPeriodLabel(raw.insurance_expense)
    },
    itExpense: {
      value: getLatestValue(raw.it_expense),
      period: getPeriodLabel(raw.it_expense)
    },
    // Raw arrays for detailed views
    revenuePeriods: revenueArr,
    grossProfitPeriods: grossProfitArr,
    grossMarginPeriods: raw.gross_margin || []
  };
}

function normalizeFlatMetrics(metrics) {
  const result = createEmptyFinancialData();
  
  if (!Array.isArray(metrics)) return result;
  
  // Group by metric name
  const byName = {};
  metrics.forEach(m => {
    const name = (m.name || '').toLowerCase().replace(/\s+/g, '_');
    if (!byName[name]) byName[name] = [];
    byName[name].push(m);
  });
  
  // Revenue
  if (byName.revenue) {
    const latest = getLatestPeriod(byName.revenue);
    result.revenue.value = latest?.value ?? null;
    result.revenue.period = latest?.period ?? null;
  }
  
  // Gross Profit
  if (byName.gross_profit) {
    const latest = getLatestPeriod(byName.gross_profit);
    result.grossProfit.value = latest?.value ?? null;
    result.grossProfit.period = latest?.period ?? null;
  }
  
  // Gross Margin
  if (byName.gross_margin) {
    const latest = getLatestPeriod(byName.gross_margin);
    result.grossMargin.value = latest?.value ?? null;
    result.grossMargin.period = latest?.period ?? null;
    result.grossMargin.calculated = false;
  } else if (result.grossProfit.value && result.revenue.value) {
    // Calculate if missing
    const calculated = calculateGrossMargin(result.grossProfit.value, result.revenue.value);
    if (calculated) {
      result.grossMargin.value = calculated.value;
      result.grossMargin.calculated = true;
    }
  }
  
  // EBITDA - prefer reported, then any variant
  const ebitdaMetrics = metrics.filter(m => 
    (m.name || '').toLowerCase().includes('ebitda')
  );
  
  const ebitdaPriority = [
    { match: 'reported', variant: 'reported' },
    { match: 'management', variant: 'management_adjusted' },
    { match: 'seller', variant: 'seller_adjusted' },
    { match: 'qoe', variant: 'qoe_adjusted' },
    { match: 'pro forma', variant: 'pro_forma_synergized' }
  ];
  
  for (const { match, variant } of ebitdaPriority) {
    const found = ebitdaMetrics.find(m => 
      (m.name || '').toLowerCase().includes(match)
    );
    if (found) {
      result.ebitda.value = found.value;
      result.ebitda.period = found.period;
      result.ebitda.variant = variant;
      break;
    }
  }
  
  // Net Debt
  const netDebt = metrics.find(m => 
    (m.name || '').toLowerCase().includes('net_debt') || 
    (m.name || '').toLowerCase().includes('net debt')
  );
  if (netDebt) {
    result.netDebt.value = netDebt.value;
    result.netDebt.period = netDebt.period;
  }
  
  // Revolver
  const revolver = metrics.find(m => 
    (m.name || '').toLowerCase().includes('revolver')
  );
  if (revolver) {
    result.revolverAvailability.value = revolver.value;
    result.revolverAvailability.period = revolver.period;
  }
  
  // DSO
  const dso = metrics.find(m => 
    (m.name || '').toLowerCase().includes('dso') ||
    (m.name || '').toLowerCase().includes('days sales outstanding')
  );
  if (dso) {
    result.dso.value = dso.value;
    result.dso.period = dso.period;
  }
  
  // Customer Concentration
  const custConc = metrics.find(m => 
    (m.name || '').toLowerCase().includes('customer_concentration') ||
    (m.name || '').toLowerCase().includes('customer concentration')
  );
  if (custConc) {
    result.customerConcentration.value = custConc.value;
    result.customerConcentration.label = 'Top Customer';
  }
  
  // Customer Count
  const custCount = metrics.find(m => 
    (m.name || '').toLowerCase().includes('customer_count') ||
    (m.name || '').toLowerCase().includes('customer count')
  );
  if (custCount) {
    result.customerCount.value = custCount.value;
    result.customerCount.period = custCount.period;
  }
  
  // Expenses
  const expenseMap = {
    lease_expense: 'leaseExpense',
    lease: 'leaseExpense',
    insurance_expense: 'insuranceExpense',
    insurance: 'insuranceExpense',
    it_expense: 'itExpense',
    it: 'itExpense',
    overtime_pct_payroll: 'overtimePctPayroll',
    overtime: 'overtimePctPayroll'
  };
  
  metrics.forEach(m => {
    const name = (m.name || '').toLowerCase().replace(/\s+/g, '_');
    const key = expenseMap[name];
    if (key && result[key]) {
      result[key].value = m.value;
      result[key].period = m.period;
    }
  });
  
  return result;
}

function createEmptyFinancialData() {
  return {
    revenue: { value: null, period: null },
    grossProfit: { value: null, period: null },
    grossMargin: { value: null, period: null, calculated: false },
    ebitda: { value: null, period: null, variant: null, variants: {} },
    netDebt: { value: null, period: null },
    revolverAvailability: { value: null, period: null },
    dso: { value: null, period: null },
    customerConcentration: { value: null, label: null, period: null },
    customerCount: { value: null, period: null },
    overtimePctPayroll: { value: null, period: null },
    leaseExpense: { value: null, period: null },
    insuranceExpense: { value: null, period: null },
    itExpense: { value: null, period: null },
    revenuePeriods: [],
    grossProfitPeriods: [],
    grossMarginPeriods: []
  };
}

export default normalizeFinancialExtraction;
